import { createChatSession, completeChatSession } from './api';

// Delay before showing a prebaked bot reply, to imitate a real person typing.
export const BOT_REPLY_DELAY = 1000;
export const CONTINUE_TOUR_BUTTON_TEXT = 'Or, Continue To Next Tour Stop';

// Schedule a bot reply, showing the "AI is thinking" indicator during the
// delay. If `startTime` is provided, the delay is reduced by however much
// real work has already elapsed since then.
export const scheduleBotReply = (dispatch, fn, startTime = Date.now()) => {
  const elapsed = Date.now() - startTime;
  const remaining = Math.max(0, BOT_REPLY_DELAY - elapsed);
  if (remaining === 0) {
    fn();
    return;
  }
  dispatch({
    type: 'UPDATE_LAST_MESSAGE',
    payload: { nextDelayMs: remaining, nextDelayDeadline: Date.now() + remaining },
  });
  dispatch({ type: 'SET_IS_AI_THINKING', payload: true });
  setTimeout(() => {
    dispatch({ type: 'UPDATE_LAST_MESSAGE', payload: { nextDelayDeadline: 0 } });
    dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
    fn();
  }, remaining);
};

// Conversation history sent to the AI for follow-up questions.
// Keep last MAX_PAIRS user+bot text exchanges; skip messages with no text
// (button-only flows, system messages). Backend caps to 8 pairs anyway.
const MESSAGE_HISTORY_MAX_PAIRS = 8;
export const buildMessageHistory = ( messages = [] ) => {
  const textMessages = messages
    .filter( ( m ) => ( m.type === 'user' || m.type === 'bot' )
      && typeof m.text === 'string'
      && m.text.trim() !== '' )
    .map( ( m ) => ( {
      role: m.type === 'user' ? 'user' : 'assistant',
      content: m.text,
    } ) );
  return textMessages.slice( -MESSAGE_HISTORY_MAX_PAIRS * 2 );
};

// Formatting
export const formatInterestsList = (interests) => {
  const items = interests.split(', ');
  return items.length > 1
    ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    : items[0];
};

// More places header variants
const MORE_PLACES_VARIANTS = ['[FIRST_NAME], here are more spots I think would be perfect for you.',
  "[FIRST_NAME], we've got even more places you'll love.",
  '[FIRST_NAME], here are a few more amenities picked just for you.',
  "[FIRST_NAME], there's more to see — here are additional spots I think you'll enjoy."];
let morePlacesIndex = 0;
export const MORE_PLACES_HEADER = () => {
  const variant = MORE_PLACES_VARIANTS[morePlacesIndex % MORE_PLACES_VARIANTS.length];
  morePlacesIndex += 1;
  return variant;
};

// Next step variants
const NEXT_STEP_VARIANTS = ['[FIRST_NAME], what would you like to do next on your [COMMUNITY_NAME] guided tour?',
  "[FIRST_NAME], there's plenty more to explore at [COMMUNITY_NAME]. What do you want to do next?",
  '[FIRST_NAME], what should we do next on your [COMMUNITY_NAME] guided tour?',
  "[FIRST_NAME], we're just getting started! What's next on your [COMMUNITY_NAME] guided tour?",
  "[FIRST_NAME], let's keep your [COMMUNITY_NAME] guided tour going.",
  '[FIRST_NAME], your [COMMUNITY_NAME] guided tour has more in store! What would you like to do next?'];
let nextStepIndex = 0;
export const NEXT_STEP_MESSAGE = () => {
  const variant = NEXT_STEP_VARIANTS[nextStepIndex % NEXT_STEP_VARIANTS.length];
  nextStepIndex += 1;
  return variant;
};

// Share interest variants
const SHARE_INTEREST_VARIANTS = ['Great! Share another interest so I can plan where to take you next at [COMMUNITY_NAME]. For example, a favorite weekend activity?',
  "Awesome! Tell me another interest and I'll pick another spot to show you around [COMMUNITY_NAME]. For example, a hobby you love?",
  'Love it! What else are you into so I can find another place you might love at [COMMUNITY_NAME]? For example, a go-to pastime?',
  "Let's keep going! Share another interest to help me curate the rest of your [COMMUNITY_NAME] tour. For example, something new you'd like to try?",
  'On it! Give me another interest so I can line up the next stop on your [COMMUNITY_NAME] tour. For example, a way you like to spend an afternoon?',
  'Sounds good! What else interests you so I can map out more of [COMMUNITY_NAME] for you? For example, a way you like to relax?',
  "Perfect! Drop another interest and I'll uncover more [COMMUNITY_NAME] spots you'll enjoy. For example, a topic you love?"];
let shareInterestIndex = 0;
export const SHARE_INTEREST_MESSAGE = () => {
  const variant = SHARE_INTEREST_VARIANTS[shareInterestIndex % SHARE_INTEREST_VARIANTS.length];
  shareInterestIndex += 1;
  return variant;
};

// Ask a question variants
const ASK_QUESTION_VARIANTS = ['Ask me anything about [COMMUNITY_NAME] or the area around us. For example, "What dining options are here?" or "Any good coffee shops nearby?"',
  'Curious about [COMMUNITY_NAME] or nearby areas? You could ask "What\'s the closest hospital?" or "Tell me about your fitness classes."',
  'Got questions about [COMMUNITY_NAME] or the area? Try something like "How far is the airport?" or "What wellness amenities do you offer?"',
  'Want to know more about [COMMUNITY_NAME] or the nearby area? You might ask "Tell me about the residences." or "What parks are close by?"',
  'Ask away about [COMMUNITY_NAME] or the area! For instance, "What grocery stores are nearby?" or "Tell me about your leadership."'];
let askQuestionIndex = 0;
export const ASK_QUESTION_MESSAGE = () => {
  const variant = ASK_QUESTION_VARIANTS[askQuestionIndex % ASK_QUESTION_VARIANTS.length];
  askQuestionIndex += 1;
  return variant;
};

export const createAirtableSession = async (sessionId, userInfo, communityNid) => {
  try {
    const sessionData = {
      sessionId,
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      phone: userInfo.phone || '',
      communityNid,
      userAgent: navigator.userAgent,
      utmSource: new URLSearchParams(window.location.search).get('utm_source') || '',
      utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
    };

    const result = await createChatSession(sessionData);

    if (!result.success) {
      console.error('[TourGuideAi] Failed to create Airtable session:', result.error);
    }

    return result.success;
  } catch (error) {
    console.error('[TourGuideAi] Error creating Airtable session:', error);
    return false;
  }
};

export const completeAirtableSession = async (sessionId, completionType = 'natural') => {
  try {
    const result = await completeChatSession(sessionId, completionType);

    if (!result.success) {
      console.error('[TourGuideAi] Failed to complete Airtable session:', result.error);
    }

    return result.success;
  } catch (error) {
    console.error('[TourGuideAi] Error completing Airtable session:', error);
    return false;
  }
};
